import React from 'react';
import { actions } from '@ecosystem/newron-design-system';
import { useUiAction } from '../../ui.actions';
import { SelectionAction, SelectionConfig, ProductGroup } from './types';
import { MAX_GROUP_ITEM_COUNT } from './constants';
import { BasicTrackingFn, EventElementType } from '../../types';
import { LeaderboardInfo } from './data';
import { findLeaderboardItemNamesForTracking, leaderboardGroupsIntoParams } from './helper';
import { TrackAnalyseTypes, trackTableClicked } from '../../analytics/utils';
import { useMobile } from '../../context/mobile/mobile.provider';
import { ContextManager } from '../../context/manager';
import './selection.bar.css';

export type Props = {
  rowsCount: number;
  rowIds: string[];
  rowIdField: string;
  rowNameField: string;
  selectedIds: string[];
  onClearSelect: () => void;
  onActionClick: () => void;
  onAddGroupClick: (selectedCount: number) => void;
  config: SelectionConfig;
  trackEvent: BasicTrackingFn;
  leaderboardData: LeaderboardInfo;
  isLoading: boolean;
  groups: ProductGroup[];
  groupsParam: string;
};

function SelectionBar({
  config,
  rowsCount,
  selectedIds,
  rowIds,
  rowIdField,
  rowNameField,
  onClearSelect,
  onActionClick,
  onAddGroupClick,
  trackEvent,
  leaderboardData,
  isLoading,
  groups,
  groupsParam,
}: Props) {
  const { perform } = useUiAction();
  const selectedCount = selectedIds.length;

  const selectedGroups = groups.filter(group => selectedIds.includes(group.id));
  const groupNames = selectedGroups.map(group => group.name);
  const numberOfGroups = `${groupNames.length}`;
  const includesGroups = groupNames.length > 0;

  const { isMobile } = useMobile();

  const expandedRowIds = selectedIds.flatMap(selectedId => {
    const group = groups.find(group => group.id === selectedId);
    return group ? group.modelIds : selectedId;
  });

  const selectedGroupsAsParam = leaderboardGroupsIntoParams(
    selectedGroups.map(group => {
      return { groupName: group.name, modelIds: group.modelIds };
    })
  );

  const handleActionClick = (actionConfig: SelectionAction) => {
    const { forcedSelection } = actionConfig;
    onActionClick();
    perform(actionConfig.action, {
      ids: forcedSelection ? rowIds.slice(0, forcedSelection) : selectedIds,
      [groupsParam]: selectedGroupsAsParam,
    });

    const expandedIds = forcedSelection ? rowIds.slice(0, forcedSelection) : expandedRowIds;

    const analyseValue = actionConfig.action.payload.settings.page.analyseOption[0];
    const analyseType = TrackAnalyseTypes[analyseValue];
    const optionChosen = findLeaderboardItemNamesForTracking(expandedIds, rowIdField, rowNameField, leaderboardData);

    if (analyseType) {
      const analyseOption = { value: analyseValue, label: analyseType } as any;
      ContextManager.getInstance().updateSettingsValues(
        {
          ['analyseOption']: [analyseOption],
        },
        false,
        true
      );
    }
    trackTableClicked(trackEvent, analyseType, EventElementType.Link, {
      optionChosen,
      numberOfProducts: `${optionChosen.length}`,
      selectedGroups: groupNames,
      numberOfGroups,
      includesGroups: `${includesGroups}`,
    });
  };

  const activeActions: SelectionAction[] = config.selectionActions.filter(action => {
    const { minSelection = 0, maxSelection } = action;
    const meetsMin = selectedCount >= minSelection;
    const meetsMax = maxSelection === undefined || selectedCount <= maxSelection;
    return meetsMin && meetsMax;
  });

  const { Button } = actions;

  const disableActionButtons = (rowIds.length === 0 && selectedIds.length === 0) || isLoading;

  const showAddToGroupButton = selectedCount > 0 && selectedCount < MAX_GROUP_ITEM_COUNT;
  return (
    <div className={`bg-medium ${isMobile ? 'py-1 px-3' : 'py-4 px-6'} flex flex-row items-center justify-between`}>
      <div className={`flex flex-row items-center ${isMobile ? 'w-full justify-between' : ''}`}>
        <div>
          {selectedCount === 0 && <span className={`inline-block ${isMobile ? '' : 'mr-4'}`}>{rowsCount} rows</span>}
          {selectedCount > 0 && (
            <span className={`inline-block ${isMobile ? '' : 'mr-4'}`}>
              {selectedCount} of {rowsCount} rows selected
            </span>
          )}
          {selectedCount > 0 && activeActions.length === 0 && (
            <span className="text-lighter">{config.outOfRangeMsg}</span>
          )}
        </div>
        <div className={`${isMobile ? 'flex flex-nowrap mobile-footer-selection-bar' : ''}`}>
          {activeActions.map((action, idx) => (
            <Button
              key={`action_${idx}`}
              mode={selectedCount > 0 ? 'secondary' : 'simple'}
              onClick={() => handleActionClick(action)}
              disabled={disableActionButtons}
            >
              {action.label}
            </Button>
          ))}
          {selectedCount > 0 && (
            <Button mode="simple" onClick={onClearSelect}>
              Reset
            </Button>
          )}
        </div>
      </div>
      {showAddToGroupButton && !isMobile && (
        <div className="flex justify-end gap-4">
          <Button
            key="addGroup_button"
            mode={'secondary'}
            onClick={() => onAddGroupClick(selectedCount)}
            disabled={disableActionButtons || includesGroups}
          >
            <Button.Icon>
              <Button.Icon.icons.AddGroup />
            </Button.Icon>
            Add to group...
          </Button>
        </div>
      )}
      {}
    </div>
  );
}

export { SelectionBar };
